<template>
<div id="app">

<MyHeader />
<MyHero
  img="hero.png"
  img_alt="seinfeld screen capture"
  title="Measuring Must-See TV"
  author="AgainWeWander"
  date="June 2020"
>
</MyHero>

<div class='section'>
  <div class='container'>
    
    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MySubtitle>So Hot Right Now</MySubtitle>
      <MyParagraph>
      Lately, I keep seeing the exact same picture. It's a picture showing IMDB ratings for Game of Thrones. The episodes are laid out in a grid with one column for each season and one block for each episode. Epsiodes are color-coded based on average IMDB ratings to create a heatmap that looks something like this:</MyParagraph>
    </MyCol>

    <MyCol myClass="is-8-desktop is-12-tablet">
      <p class='chart-title'>Game of Thrones</p>
      <p class='chart-subtitle'>Average IMDB Rating by Season</p>
      <EpisodeGrid :showData="allShowData['Game of Thrones']" activeShowName="Game of Thrones" :config="configs.episodeGrid" :resize="resize"/>
    </MyCol>

      
    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        This heatmap does a good job of showing key features of the data. You can see the stark decline in ratings as cells shift from blue-green to orange-red and, similarly, you can pick out the highest rated episodes by finding the darkest blues in the sea of greens.
      </MyParagraph>

      <MyParagraph>
        But, while heatmaps are good at outing outliers, they have some drawbacks. For one, it is difficult to compare differences in colors. It's hard to tell whether ratings are gradually increasing or decreasing throughout the early seasons. And even when the colors are clearly different (e.g., during the season 8 decline) it's hard to judge the magnitude of that difference. Just how much worse is maroon than orange than pale yellow?
      </MyParagraph>

      <MyParagraph>
        After seeing one too many of these heatmaps, I thought it would be worthwhile to explore other ways of visualizing these data. Like the heatmap, they all have their strengths and weaknesses, but it can be helpful to see them first-hand.
      </MyParagraph>

      <MyParagraph>
        Let's take a look.
      </MyParagraph>
      
      <MySubtitle>Back to Basics</MySubtitle>
      <MyParagraph>
        Behind all of these heatmaps is a fairly straightforward question: "How do TV show ratings change over time?" The simplest way to visualize change over time is a line chart. Here's one for Game of Thrones. Each line segment represents a full season; alternate seasons are shaded light and dark to help differentiate them from one another.
      </MyParagraph>
    </MyCol>

    <MyCol myClass='is-8-desktop is-12-tablet'>
      <p class='chart-title'>Game of Thrones</p>
      <p class='chart-subtitle'>Average IMDB Rating by Season</p>
      <LineChart :showData="allShowData['Game of Thrones']" activeShowName="Game of Thrones" :config="configs.largeLineRatings" :resize="resize" />
    </MyCol>

    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        Although the data in this chart are identical to those in the heatmap above, the story looks a little different. For example, if you look at any one season, you can see that user ratings tend to climb from the first episodes of the season to the last. Starting in season 5 you start to see more variation in the ratings for episodes. And, looking at the final season, you can see just how drastically ratings have fallen. The best rated episode of season 8 has a lower rating than the worst rated episode from seasons 1 through 7.
      </MyParagraph>

      <MySubtitle>More is Better</MySubtitle>
      <MyParagraph>If we want to know how TV show ratings change over time, it also helps to look at more than one show. So, with that in mind, I pulled the IMDB ratings from 20 popular shows for comparison.</MyParagraph>

      <MyParagraph>
        To get a sense of just how much fans disliked the final episode of Game of Thrones, not one of the over 2,500 episodes I pulled had a worse rating than "The Iron Throne".
      </MyParagraph>

      <MyParagraph>Here are a series of line charts for each show. All y-axes are scaled from a minimum of 4 stars (the lowest rating in the data) to a maximum of 10 stars. Because every show has a different number of episodes, the x-axes are scaled so that the first episode of each show appears at the far left and the last (or most recent) episode appears at the far right.</MyParagraph>
    </MyCol>

    <!-- grid! -->
    <div class='columns' v-for="i in [0, 1, 2, 3, 4]" :key="'ratings' + i">
      <div class='column is-half' >
        <div class='columns is-mobile'>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[0+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[0+4*i]]" :activeShowName="allShowNames[0+4*i]" :config="configs.smallLineRatings" :resize="resize" />
          </div>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[1+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[1+4*i]]" :activeShowName="allShowNames[1+4*i]" :config="configs.smallLineRatings" :resize="resize" />
          </div>
        </div>
      </div>
      <div class='column is-half'>
        <div class='columns is-mobile'>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[2+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[2+4*i]]" :activeShowName="allShowNames[2+4*i]" :config="configs.smallLineRatings" :resize="resize" />
          </div>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[3+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[3+4*i]]" :activeShowName="allShowNames[3+4*i]" :config="configs.smallLineRatings" :resize="resize" />
          </div>
        </div>
      </div>
    </div>

    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        A few things stand out here. First, all of these shows are remarkably well rated. The median IMDB rating across all episodes is an 8.3 out of 10. The highest rated show is Breaking Bad (avg. episode rating of 9.0) and the lowest rated show is The Big Bang Theory (avg. episode rating of 7.9). 30 Rock <LineChart :showData="allShowData['30 Rock']" activeShowName="30 Rock" :config="configs.sparklineRatings" :resize="resize" /> is the most consistently well-rated show. Over the course of seven seasons and 138 episodes the show averaged an 8.0 rating with a standard deviation of just .3 points.
      </MyParagraph>

      <MyParagraph>
        This is remarkable when compared to shows like Buffy <LineChart :showData="allShowData['Buffy the Vampire Slayer']" activeShowName="Buffy the Vampire Slayer" :config="configs.sparklineRatings" :resize="resize" /> which ran for about the same amount of time (144 episodes) and earned about the same average episode rating (8.1), but had more than double the variation (standard deviation of .8 points). Look to Joss Whedon for high highs and low lows; turn to Tina Fey when you need a gauaranteed laugh.
      </MyParagraph>

      <MyParagraph>
        We can also see a handful of shows that suffer the same fate as Game of Thrones. The ratings for How I Met Your Mother are fairly consistent until a turbulent finale and the ratings for Dexter <LineChart :showData="allShowData['Dexter']" activeShowName="Dexter" :config="configs.sparklineRatingsDexter" :resize="resize" /> collapse in the final season. In contrast, shows like Sopranos, The Big Bang Theory, and The Office (US) <LineChart :showData="allShowData['The Office (US)']" activeShowName="The Office (US)" :config="configs.sparklineRatings" :resize="resize" /> end on a high note, with the final episodes receiving the best ratings of the series.
      </MyParagraph>
    </MyCol>

    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MySubtitle>Popularity Contest</MySubtitle>
      <MyParagraph>
        So far we've only looked at user ratings, but what about the other definition of TV ratings? How many people actually watched these shows? Here are the <a href="https://www.nielsen.com/us/en/solutions/measurement/television/">Nielson ratings</a> for Game of Thrones courtesy of Wikipedia.
      </MyParagraph>
    </MyCol>


    <MyCol myClass='is-8-desktop is-12-tablet'>
      <p class='chart-title'>Game of Thrones</p>
      <p class='chart-subtitle'>Average Number of US Viewers by Season</p>
      <LineChart :showData="allShowData['Game of Thrones']" activeShowName="Game of Thrones" :config="configs.largeLineViewers" :resize="resize" />
    </MyCol>

    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        As user ratings fell, the audience for Game of Thrones soared. The final season had nearly fourteen times more viewers than the first season and the final episodes were the most-watched in the series. 
      </MyParagraph>

      <MyParagraph>
        For comparison, let's look at the other shows in our dataset. Most of the 20 shows have audience data, though I couldn't find complete Nielsen ratings for Buffy, It's Always Sunny in Philadelphia, Sopranos, or The Wire. Similarly, there are no viewer numbers for the final seasons of Arrested Development and Community because those shows moved from TV broadcast to online release.
      </MyParagraph>

      <MyParagraph>
        Finally, because ratings vary wildly from show to show (e.g., Friends averaged over 25 million viewers per episode while Mad Men averaged just 2 million), the y-axes are scaled to each show's largest audience.
      </MyParagraph>
    </MyCol>


    <!-- grid! -->
    <div class='columns' v-for="i in [0, 1, 2, 3, 4]" :key="'viewers' +i">
      <div class='column is-half' >
        <div class='columns is-mobile'>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[0+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[0+4*i]]" :activeShowName="allShowNames[0+4*i]" :config="configs.smallLineViewers" :resize="resize" />
          </div>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[1+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[1+4*i]]" :activeShowName="allShowNames[1+4*i]" :config="configs.smallLineViewers" :resize="resize" />
          </div>
        </div>
      </div>
      <div class='column is-half'>
        <div class='columns is-mobile'>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[2+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[2+4*i]]" :activeShowName="allShowNames[2+4*i]" :config="configs.smallLineViewers" :resize="resize" />
          </div>
          <div class='column is-half'>
            <p class='chart-title'>{{ allShowNames[3+4*i] }}</p>
            <LineChart :showData="allShowData[allShowNames[3+4*i]]" :activeShowName="allShowNames[3+4*i]" :config="configs.smallLineViewers" :resize="resize" />
          </div>
        </div>
      </div>
    </div>


    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        There are so many crazy trends here. You can see the growing hype for shows like Game of Thrones and Breaking Bad <LineChart :showData="allShowData['Breaking Bad']" activeShowName="Breaking Bad" :config="configs.sparklineViewers" :resize="resize" /> as the number of viewers climbs higher and higher each season. You can also see the outsized impact that the Super Bowl has on ratings by looking at the massive spikes for episodes of Brooklyn Nine-Nine, Friends, and The Office (US) <LineChart :showData="allShowData['The Office (US)']" activeShowName="The Office (US)" :config="configs.sparklineViewers" :resize="resize" /> that aired after the big game. Mad Men <LineChart :showData="allShowData['Mad Men']" activeShowName="Mad Men" :config="configs.sparklineViewers" :resize="resize" /> really knows how to market a season premier. It's Always Sunny <LineChart :showData="allShowData[sunny]" :activeShowName="sunny" :config="configs.sparklineViewers" :resize="resize" /> audiences peaked several seasons ago. And no finale comes anywhere near the Seinfeld <LineChart :showData="allShowData['Seinfeld']" activeShowName="Seinfeld" :config="configs.sparklineViewers" :resize="resize" /> finale <a href="https://www.mentalfloss.com/article/24673/10-most-watched-series-finales-ever" class='mash'>(except for M*A*S*H and Cheers and The Fugitive)</a>.
      </MyParagraph>


      <MyParagraph>
        The Seinfeld finale is such an outlier that it's worth putting all of the shows together on a single chart for comparison. In the chart below each show is represented by a single line that extends from the first episode to the last. The longer the line, the more episodes in the show.
      </MyParagraph>
    </MyCol>

    <MyCol myClass='is-8-desktop is-12-tablet'>
      <div class='control has-text-left'>
        <div class='select is-info'>
          <select class='styled-select' v-model="viewerChartShow">
            <option v-for="show  in allShowNames" :key="show">{{ show }}</option>
          </select>
        </div>
      </div>
      <p class='chart-subtitle'>Average Number of US Viewers by Season</p>
      <div class='chart-hover-div'> 
        <p class='chart-hover-instructions is-hidden-touch'>hover or click lines to change show</p>
      </div>
      <LineChart :showData="rawShowData" :activeShowName="viewerChartShow" :config="configs.compareLineViewers" :resize="resize" @setActiveShowName="setViewerChartShow" />
    </MyCol>


    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        Seinfeld clearly stands out from the crowd. The only show even close is Friends, whose early seasons actually brought in a larger audience than the early seasons of Seinfeld. But the audiences for more recent network comedies are considerably smaller (e.g., The Big Bang Theory averaged 14.2 million; How I Met Your Mother averaged 9 million). And non-network shows fare even worse (e.g., Breaking Bad and Mad Men averaged fewer than 3 million viewers per episode).
      </MyParagraph>


      <MySubtitle>Bubbling Up</MySubtitle>
      <MyParagraph>
        Up to this point, we've only been looking at one measure of "ratings" over time. But it would be nice to see both user scores and audience size plotted together.
      </MyParagraph>

      <MyParagraph>
        Here is a bubble chart inspired by <a href="https://truth-and-beauty.net/projects/remixing-rosling">this redesign</a> of Hans Rosling's Health and Wealth charts. The y-axis shows average IMDb ratings. The x-axis shows the number of viewers. Each episode is plotted as a bubble. The size and color of each bubble tells us when an episode aired. The first episode of a show appears as tiny pale gray dot. Each subsequent episode is slightly larger and slightly darker, until the final episode is shown as the largest bubble on the screen, colored dark purple. 
      </MyParagraph>
    </MyCol>

    <MyCol myClass='is-8-desktop is-12-tablet'>
      <div class='control has-text-left'>
        <div class='select is-info'>
          <select class='styled-select' v-model="viewerChartShow">
            <option v-for="show in allShowNames" :key="'b'+show">{{ show }}</option>
          </select>
        </div>
      </div>
      <p class='chart-subtitle'>Average IMDB Rating by Average Viewers per Episode</p>
      <BubbleChart :showData="allShowData[viewerChartShow]" :activeShowName="viewerChartShow" :config="configs.largeBubble" :resize="resize" />
    </MyCol>


   <MyCol myClass='is-6-desktop is-8-tablet'>
      <MyParagraph>
        Although a bit strange to decode, this bubble chart brings out patterns that are otherwise hard to find. Each corner of the chart represents something different. Ideally, as the bubbles grow in size they should shift towards the top right of the chart (i.e., high ratings, large audiences). A shift towards the bottom right corner is a bit more tragic (i.e., growing audiences, terrible reviews). And a shift towards the bottom left corner suggests the end is near (i.e., bad reviews, shrinking audiences)
      </MyParagraph> 

      <MyParagraph>
        Looking at Seinfeld <BubbleChart :showData="allShowData['Seinfeld']" activeShowName="Seinfeld" :config="configs.sparklineBubble" :resize="resize" /> we see the bubbles shift from left to right (i.e., audiences kept growing) but the bubbles don't really move up or down (i.e., user ratings stayed the same). On the flip side, 30 Rock <BubbleChart :showData="allShowData['30 Rock']" activeShowName="30 Rock" :config="configs.sparklineBubble" :resize="resize" /> shows the opposite pattern. The bubbles don't move up or down but they do move from right to left, indicating that user ratings stayed consistent but audiences dropped from episode to episode. 
      </MyParagraph>

      <MyParagraph>
        The bubble chart for The Big Bang Theory <BubbleChart :showData="allShowData['The Big Bang Theory']" activeShowName="The Big Bang Theory" :config="configs.sparklineBubble" :resize="resize" /> is one of the most interesting. As the bubbles grow, they travel in a sweeping boomerang shape. The small bubbles (i.e., the early episodes) move from left to right, indicating that more people were tuning in over time. But as the bubbles grow even larger, they swing back to the left and gradually fall downwards, suggesting that later episodes were losing viewers and also dropping in quality from week to week.   
      </MyParagraph>

      <MyParagraph>
        That said, I'm still not convinced that the bubble chart is any better than two line charts plotted together.
      </MyParagraph>
    </MyCol>

    <MyCol myClass='is-8-desktop is-12-tablet'>
      <div class='control has-text-left'>
        <div class='select is-info'>
          <select class='styled-select' v-model="viewerChartShow">
            <option v-for="show in allShowNames" :key="'b'+show">{{ show }}</option>
          </select>
        </div>
      </div>
      <p class='chart-subtitle'>Average IMDB Rating and Average Viewers per Episode</p>
      <LineChart :showData="allShowData[viewerChartShow]" :activeShowName="viewerChartShow" :config="configs.doubleLine" :resize="resize" />
    </MyCol>


    <MyCol myClass='is-6-desktop is-8-tablet'>
      <MySubtitle>Full Circle</MySubtitle>
      <MyParagraph>
        There are undoubtedbly other interesting ways to think about these data. I'll leave that to you. For now, I simply ask that everyone <a href="https://i.redd.it/0mkad9wf5fr41.png">stop making Game of Thrones heatmaps</a>. <a href="https://i.redd.it/xqncdxjmsp651.png">Please.</a> <a href="https://i.redd.it/z9qgu2kv1lr41.png">I beg you</a>.
      </MyParagraph>

      <MyParagraph>
        If you want to see more articles like this one, check out my site <a href="https://againwewander.com">AgainWeWander</a>. And, if you still want more heatmaps, well, who am I to judge. Have at it.
      </MyParagraph>
    </MyCol>


   <MyCol myClass='is-8-desktop is-12-tablet'>
    <div class='control has-text-left'>
      <div class='select is-info'>
        <select class='styled-select' v-model="viewerChartShow">
          <option v-for="show in allShowNames" :key="'c'+show">{{ show }}</option>
        </select>
      </div>
    </div>
    <p class='chart-subtitle'>Average IMDB Rating by Season</p>
    <EpisodeGrid :showData="allShowData[viewerChartShow]" :activeShowName="viewerChartShow" :config="configs.episodeGrid" :resize="resize"/>
    </MyCol>

    <MyCol myClass='is-6-desktop is-8-tablet'>
      <div class='data-notes-div'>
        <hr>
        <p class='title is-5'>Notes</p>
        <div class='data-notes-text'>
          <p>
            All data for average user ratings courtesy of <a href="https://www.imdb.com/">IMDB</a>. All data for average audience size courtesy of <a href="https://en.wikipedia.org/">Wikipedia</a>. Data were collected in early June, 2020.
          </p>

          <p>
            To build the final dataset, I started by creating episode lists for each show from Wikipedia. Wikipedia has a general template <a href="https://en.wikipedia.org/wiki/List_of_Game_of_Thrones_episodes">"List of [Show] episodes"</a> that they use to catalog episode titles, sequence, air dates, and viewer numbers. I then cross-referenced this list against <a href="https://www.imdb.com/title/tt0944947/episodes?season=8&ref_=tt_eps_sn_8">IMDB pages for each season</a>. 
          </p>

          <p>
            Occasionally the two sites disagreed on episode counts, usually due to special 2-part episodes. In cases where Wikipedia included two separate episodes but IMDB did not, I collapsed the data into a single entry (e.g., the first few episodes of season 4 of The Office). An exception to this rule was made for the 30 Rock finale. Wikipedia lists two separate episodes for the finale, while IMDB collapses them into one. I included both records because Wikipedia lists a different title and different writers for each episode.
          </p>

          <p>
            Finally, I renumbered episodes/seasons sequentially within the dataset to prevent gaps in charting. As a result, my episode numbers may slightly differ from other datasets. 
          </p>
          
        </div>
      </div>
    </MyCol>



  </div>
</div>


<MyFooter />


</div>
</template>

<script>

import { csv } from 'd3-fetch';
import uniq from 'lodash.uniq';
import map from 'lodash.map';
import debounce from 'lodash.debounce';

import { configs } from './js/configs.js';
import { filterObj } from './js/helpers.js';

import MyHeader from './components/MyHeader.vue';
import MyHero from './components/MyHero.vue';
import MyCol from './components/MyCol.vue';
import MySubtitle from './components/MySubtitle.vue';
import MyParagraph from './components/MyParagraph.vue';
import EpisodeGrid from './components/EpisodeGrid.vue';
import LineChart from './components/LineChart.vue';
import BubbleChart from './components/BubbleChart.vue';
import MyFooter from './components/MyFooter.vue';


export default {
  name: 'App',
  components: {
    MyHeader, MyHero, 
    MyCol, MySubtitle, MyParagraph,
    EpisodeGrid, LineChart, BubbleChart,
    MyFooter
  },
  data() {
    return {
      sunny: "It's Always Sunny in Philadelphia",
      rawShowData: [],
      allShowData: [],
      isLoaded: false,
      windowWidth: 0,
      col10width: 0,
      col6width: 0,
      col3width: 0,
      configs: configs,
      activeShowName: '',
      allShowNames: [],
      viewerChartShow: 'Seinfeld',
      resize: new Date()
    }
  },
  created() {
    window.addEventListener('resize', debounce(this.handleResize, 100));
    this.getData();
  },  
  mounted() {
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.resize = new Date();
    },
    setViewerChartShow(showName) {
      this.viewerChartShow = showName;
    },
    async getData() {
      // load the CSV
      const uri = '/data.csv'
      let data = await csv(uri, function(d) {
        return {
          ep: +d.ep,
          id: +d.id,
          rating: +d.rating,
          se: +d.se,
          show: d.show,
          title: d.title,
          viewers: d.viewers === '' ? null : +d.viewers
        };
      }).then(function(data) {
        return data;
      });

      // reformat so that the data is key:dict with show name as key
      const showNames = uniq(map(data, 'show'))
      let showDict = {}
      for (let i=0; i<showNames.length; i++) {
        const showName = showNames[i];
        const showData = filterObj(data, { 'show': showName })
        showDict[showName] = showData;
      }

      // save the data to the instance
      this.isLoaded = true;
      this.rawShowData = data;
      this.allShowData = showDict;
      this.allShowNames = Object.keys(showDict);
    }
  }
}
</script>


<style lang='scss'>


#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}


</style>
